import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { ADMIN_SUBSCRIPTION_PLANS_QUERY_KEY } from '@/state/queries/admin/use-admin-subscription-plans-query';
import { GET_SUBSCRIPTION_PLANS } from '@/state/queries/transactions/use-subscription-plans';
import type {
  SubscriptionCategory,
  SubscriptionPlan,
  SubscriptionTier,
} from '@/types/dtos/transactions/subscription-plan.dto';

export type CreateSubscriptionPlanBody = {
  tier: SubscriptionPlan['tier'];
  entityType: 'Organization' | 'Event';
  billingPeriod?: SubscriptionPlan['billingPeriod'];
  price: number;
  features?: Record<string, string | number | boolean>;
  includedQuotas?: { quotaKey: string; quantity: number }[];
  maxQuotas?: { quotaKey: string; quantity: number }[];
  overagePrices?: { quotaKey: string; pricePerUnit: number }[];
  featureKeys?: string[];
  subscriptionProductKey?: string;
  subscriptionCategory?: SubscriptionCategory;
  requiresParent?: boolean;
  parentProductKey?: string;
  minParentTier?: SubscriptionTier;
};

export function useCreateSubscriptionPlanMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateSubscriptionPlanBody) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/transactions/subscription-plans`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body,
        token: token ?? '',
      }) as Promise<SubscriptionPlan>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ADMIN_SUBSCRIPTION_PLANS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [GET_SUBSCRIPTION_PLANS] });
    },
  });
}
